<script>
  import { onMount, onDestroy, untrack } from 'svelte'
  import { flip } from 'svelte/animate'
  import { dndzone } from 'svelte-dnd-action'
  import EditableTitle from './EditableTitle.svelte'
  import KanbanBoard from './KanbanBoard.svelte'
  import { csrfToken } from '../lib/csrf.js'
  import { reduceMotion, flipDurationMs } from '../lib/motion.js'
  import { subscribeToNeurons } from '../lib/cable.js'
  import { markdownPreview } from '../lib/markdown.js'

  let { parentId = null, onNavigate = () => {} } = $props()
  let neurons = $state([])
  let normalItems = $state([])
  let kanbanNeurons = $state([])
  let normalDragging = $state(false)
  let newTitle = $state('')
  let loading = $state(true)
  let savedNeuronId = $state(null)
  let savedTimer = null
  let countdowns = $state({})
  let countdownIntervals = {}
  let fadingOut = $state({})

  const DISCARD_GRACE_SECONDS = 10

  function replaceNeuron(updated) {
    neurons = neurons.map(n => n.id === updated.id ? updated : n)
  }

  $effect(() => {
    if (normalDragging) return
    const currentNormal = untrack(() => normalItems)
    const newNormal = neurons.filter(n => n.doing_lifecycle !== 'kanban')
    const normalMap = new Map(newNormal.map(n => [n.id, n]))
    const existingIds = new Set(currentNormal.map(n => n.id))
    const kept = currentNormal
      .filter(n => normalMap.has(n.id))
      .map(n => normalMap.get(n.id))
    const added = newNormal.filter(n => !existingIds.has(n.id))
    normalItems = [...added, ...kept]
    kanbanNeurons = neurons.filter(n => n.doing_lifecycle === 'kanban')
  })

  async function fetchNeurons() {
    const url = parentId
      ? `/api/neurons?parent_id=${parentId}`
      : '/api/neurons'

    const response = await fetch(url)
    if (response.ok) {
      neurons = await response.json()
      for (const n of neurons) {
        if (n.flagged_for_discard_at) {
          startCountdown(n.id, n.flagged_for_discard_at)
        }
      }
    }
    loading = false
  }

  async function createNeuron() {
    const title = newTitle.trim()
    if (!title) return

    const response = await fetch('/api/neurons', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({ title, parent_id: parentId }),
    })

    if (response.ok) {
      const neuron = await response.json()
      neurons = [neuron, ...neurons]
      newTitle = ''
      autoResize()
    }
  }

  let textareaEl = $state(null)
  let autocompleteDismissed = $state(false)
  let autocompleteIndex = $state(0)

  const slashCommands = [
    { command: '/do', label: 'Mark as doing' },
    { command: '/kanban', label: 'Add to kanban board' },
  ]

  let autocompleteMatches = $derived.by(() => {
    const match = newTitle.match(/(?:^|\s)(\/\S*)$/)
    if (!match) return []
    const partial = match[1].toLowerCase()
    return slashCommands.filter(c => c.command.startsWith(partial) && c.command !== partial)
  })

  function autoResize() {
    if (!textareaEl) return
    textareaEl.style.height = 'auto'
    textareaEl.style.height = textareaEl.scrollHeight + 'px'
  }

  function applyAutocomplete(command) {
    newTitle = newTitle.replace(/(?:^|\s)(\/\S*)$/, (match, partial) => {
      const prefix = match.startsWith(' ') ? ' ' : ''
      return prefix + command + ' '
    })
    autocompleteDismissed = true
    textareaEl?.focus()
  }

  let autocompleteVisible = $derived(!autocompleteDismissed && autocompleteMatches.length > 0)

  function handleKeydown(event) {
    if (autocompleteVisible) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        autocompleteIndex = (autocompleteIndex + 1) % autocompleteMatches.length
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        autocompleteIndex = (autocompleteIndex - 1 + autocompleteMatches.length) % autocompleteMatches.length
        return
      }
      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault()
        applyAutocomplete(autocompleteMatches[autocompleteIndex].command)
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        autocompleteDismissed = true
        return
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      createNeuron()
    }
  }

  function handleInput() {
    autoResize()
    autocompleteDismissed = false
    autocompleteIndex = 0
  }

  function showSaved(neuronId) {
    clearTimeout(savedTimer)
    savedNeuronId = neuronId
    savedTimer = setTimeout(() => { savedNeuronId = null }, 1000)
  }

  async function flagNeuron(neuronId) {
    const response = await fetch(`/api/neurons/${neuronId}/discard`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': csrfToken() },
    })

    if (response.ok) {
      const updated = await response.json()
      replaceNeuron(updated)
      startCountdown(neuronId, updated.flagged_for_discard_at)
    }
  }

  async function unflagNeuron(neuronId) {
    const response = await fetch(`/api/neurons/${neuronId}/unflag`, {
      method: 'PATCH',
      headers: { 'X-CSRF-Token': csrfToken() },
    })

    if (response.ok) {
      const updated = await response.json()
      replaceNeuron(updated)
      stopCountdown(neuronId)
    }
  }

  async function transitionNeuron(neuronId, target, lifecycle = null) {
    neurons = neurons.map(n => {
      if (n.id !== neuronId) return n
      if (target === 'none') {
        return { ...n, doing_at: null, doing_lifecycle: null, doing_phase: null, flagged_for_discard_at: null }
      }
      return {
        ...n,
        doing_at: n.doing_at || new Date().toISOString(),
        doing_lifecycle: lifecycle || n.doing_lifecycle,
        doing_phase: target,
        flagged_for_discard_at: target === 'done' ? (n.flagged_for_discard_at || new Date().toISOString()) : null,
      }
    })

    const body = { target }
    if (lifecycle) body.lifecycle = lifecycle

    const response = await fetch(`/api/neurons/${neuronId}/transition`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify(body),
    })

    if (response.ok) {
      const updated = await response.json()
      replaceNeuron(updated)
      if (updated.flagged_for_discard_at) {
        startCountdown(neuronId, updated.flagged_for_discard_at)
      } else {
        stopCountdown(neuronId)
      }
    }
  }

  function toggleDoingNeuron(neuron) {
    if (neuron.flagged_for_discard_at) {
      transitionNeuron(neuron.id, 'todo')
    } else {
      transitionNeuron(neuron.id, 'done')
    }
  }

  function startCountdown(neuronId, flaggedAt) {
    stopCountdown(neuronId)
    const flaggedTime = new Date(flaggedAt).getTime()

    function tick() {
      const elapsed = (Date.now() - flaggedTime) / 1000
      const remaining = Math.max(0, DISCARD_GRACE_SECONDS - elapsed)
      countdowns = { ...countdowns, [neuronId]: Math.ceil(remaining) }

      if (remaining <= 0) {
        stopCountdown(neuronId)
        fadeOutAndRemove(neuronId)
      }
    }

    tick()
    if (!reduceMotion) {
      countdownIntervals[neuronId] = setInterval(tick, 1000)
    }
  }

  const FADE_OUT_MS = 350

  function fadeOutAndRemove(neuronId) {
    if (reduceMotion) {
      neurons = neurons.filter(n => n.id !== neuronId)
      return
    }

    fadingOut = { ...fadingOut, [neuronId]: true }
    setTimeout(() => {
      const { [neuronId]: _, ...rest } = fadingOut
      fadingOut = rest
      neurons = neurons.filter(n => n.id !== neuronId)
    }, FADE_OUT_MS)
  }

  function stopCountdown(neuronId) {
    clearInterval(countdownIntervals[neuronId])
    delete countdownIntervals[neuronId]
    const { [neuronId]: _, ...rest } = countdowns
    countdowns = rest
  }

  async function saveNeuronTitle(neuronId, newTitleValue) {
    const response = await fetch(`/api/neurons/${neuronId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({ title: newTitleValue }),
    })

    if (response.ok) {
      const updated = await response.json()
      replaceNeuron(updated)
    }
  }

  function handleConsider(e) {
    normalDragging = true
    normalItems = e.detail.items
  }

  async function handleFinalize(e) {
    normalItems = e.detail.items
    normalDragging = false

    for (const item of normalItems) {
      if (item.doing_lifecycle === 'kanban') {
        transitionNeuron(item.id, 'none')
        await reorderOnServer(parentId, normalItems.map(n => n.id))
        return
      }
    }

    await reorderOnServer(parentId, normalItems.map(n => n.id))
  }

  async function reorderOnServer(pid, neuronIds) {
    await fetch('/api/neurons/reorder', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken(),
      },
      body: JSON.stringify({
        parent_id: pid,
        neuron_ids: neuronIds,
      }),
    })
  }

  let cableSubscription = null

  function handleCableMessage(data) {
    if (data.type === 'created') {
      if (data.parent_id == parentId || (!data.parent_id && !parentId)) {
        const existing = neurons.find(n => n.id === data.neuron.id)
        if (!existing) {
          neurons = [data.neuron, ...neurons]
        }
      }
    } else if (data.type === 'updated') {
      replaceNeuron(data.neuron)
    } else if (data.type === 'discarded') {
      replaceNeuron(data.neuron)
      if (data.neuron.flagged_for_discard_at) {
        startCountdown(data.neuron.id, data.neuron.flagged_for_discard_at)
      }
    } else if (data.type === 'reordered') {
      if (data.parent_id == parentId || (!data.parent_id && !parentId)) {
        fetchNeurons()
      }
    }
  }

  onMount(() => {
    fetchNeurons()
    autoResize()
    cableSubscription = subscribeToNeurons(handleCableMessage, fetchNeurons)
  })

  onDestroy(() => {
    cableSubscription?.unsubscribe()
  })

  $effect(() => {
    parentId
    fetchNeurons()
  })
</script>

<div>
  <div class="mb-6 relative">
    <textarea
      bind:this={textareaEl}
      bind:value={newTitle}
      onkeydown={handleKeydown}
      oninput={handleInput}
      placeholder="New neuron..."
      autocomplete="off"
      rows="1"
      class="w-full rounded-lg pr-4 py-3 text-base outline-none transition-colors placeholder:opacity-50 resize-none overflow-hidden bg-surface-0 text-text border border-border min-h-[44px]"
      style="padding-left: calc(0.75rem + 20px + 0.5rem);"
    ></textarea>
    {#if autocompleteVisible}
      <ul
        data-testid="slash-autocomplete"
        class="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-border bg-surface-1 py-1 text-sm shadow-lg shadow-black/20"
      >
        {#each autocompleteMatches as match, i}
          <li>
            <button
              onclick={() => applyAutocomplete(match.command)}
              class="w-full px-3 py-1.5 text-left cursor-pointer flex items-center gap-2 transition-colors"
              class:bg-surface-2={i === autocompleteIndex}
            >
              <span class="font-mono text-synapse">{match.command}</span>
              <span class="text-text-muted">{match.label}</span>
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>

  {#if !loading && neurons.length === 0}
    <div data-testid="empty-neuron-list"></div>
  {:else}
    {#if kanbanNeurons.length > 0}
      <KanbanBoard
        neurons={kanbanNeurons}
        {parentId}
        onTransition={transitionNeuron}
        onNavigate={onNavigate}
        onReorder={reorderOnServer}
        onSaveTitle={saveNeuronTitle}
        onShowSaved={showSaved}
        {countdowns}
        {fadingOut}
        {slashCommands}
      />
    {/if}

    {#if normalItems.length > 0 || kanbanNeurons.length > 0}
      <ul
        data-testid="neuron-list"
        class="space-y-2"
        class:empty-drop-zone={normalItems.length === 0}
        use:dndzone={{ items: normalItems, flipDurationMs, type: 'neuron', dropTargetStyle: {}, dragHandleSelector: '[data-drag-handle]', delayTouchStart: 200 }}
        onconsider={handleConsider}
        onfinalize={handleFinalize}
      >
        {#each normalItems as neuron (neuron.id)}
          <li
            data-neuron-id={neuron.id}
            animate:flip={{ duration: flipDurationMs }}
            class="group relative flex items-start gap-2 rounded-lg px-3 py-3 transition-colors bg-surface-0 border border-border"
            class:discarded={neuron.discarded_at}
            class:flagged={neuron.flagged_for_discard_at && !neuron.discarded_at}
            class:fading-out={fadingOut[neuron.id]}
          >
            {#if neuron.doing_at && neuron.doing_lifecycle === 'classic'}
              <!-- Classic doing neurons: no discard/undo buttons, checkbox IS the toggle -->
            {:else if neuron.flagged_for_discard_at && !neuron.discarded_at}
              <button
                data-testid="undo-button"
                onclick={() => unflagNeuron(neuron.id)}
                class="absolute -left-7 text-text-muted opacity-60 hover:!opacity-100 transition-opacity cursor-pointer"
                aria-label="Undo discard {neuron.title}"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7h6a4 4 0 010 8H7"/>
                  <path d="M5 5L3 7l2 2"/>
                </svg>
              </button>
            {:else if !neuron.discarded_at}
              <button
                data-testid="discard-button"
                onclick={() => flagNeuron(neuron.id)}
                class="absolute -left-7 text-text-muted opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity cursor-pointer"
                aria-label="Discard {neuron.title}"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2 4h12M5.33 4V2.67a1.33 1.33 0 011.34-1.34h2.66a1.33 1.33 0 011.34 1.34V4M12 4v9.33a1.33 1.33 0 01-1.33 1.34H5.33A1.33 1.33 0 014 13.33V4"/>
                </svg>
              </button>
            {/if}

            {#if neuron.doing_at && neuron.doing_lifecycle === 'classic'}
              <span
                data-testid="do-checkbox"
                role="checkbox"
                aria-checked={!!neuron.flagged_for_discard_at}
                aria-label="{neuron.flagged_for_discard_at ? 'Uncheck' : 'Check'} {neuron.title}"
                onclick={() => toggleDoingNeuron(neuron)}
                class="select-none min-w-[44px] min-h-[44px] flex items-center justify-center text-synapse cursor-pointer"
                tabindex="0"
              >
                {#if neuron.flagged_for_discard_at}
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="2" width="14" height="14" rx="2" fill="currentColor" opacity="0.15"/>
                    <rect x="2" y="2" width="14" height="14" rx="2"/>
                    <path d="M5.5 9.5l2.5 2.5 4.5-5" stroke-width="2"/>
                  </svg>
                {:else}
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="2" width="14" height="14" rx="2"/>
                  </svg>
                {/if}
              </span>
            {:else}
              <span
                data-drag-handle
                aria-label="Drag to reorder"
                class="select-none text-lg leading-none transition-all min-w-[44px] min-h-[44px] flex items-center justify-center cursor-grab active:cursor-grabbing"
                class:opacity-40={savedNeuronId !== neuron.id}
                class:hover:opacity-70={savedNeuronId !== neuron.id}
                style="color: {savedNeuronId === neuron.id ? 'var(--color-synapse)' : 'var(--color-text-muted)'};"
                tabindex="0"
              >
                {#if savedNeuronId === neuron.id}
                  <svg class="inline-block text-node" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 8.5l3.5 3.5 6.5-7"/>
                  </svg>
                {:else}
                  ⠿
                {/if}
              </span>
            {/if}

            <div class="flex-1 min-w-0">
              <EditableTitle
                title={neuron.title}
                onSave={(newValue) => saveNeuronTitle(neuron.id, newValue)}
                onSaved={() => showSaved(neuron.id)}
                testId="neuron-title"
                class="text-base"
                {slashCommands}
              />
              {#if neuron.body}
                <p class="text-sm text-text-muted truncate mt-0.5">{markdownPreview(neuron.body)}</p>
              {/if}
            </div>

            {#if countdowns[neuron.id] != null}
              <span
                data-testid="countdown"
                class="shrink-0 text-xs tabular-nums text-text-muted"
              >{countdowns[neuron.id]}s</span>
            {:else}
              <button
                data-testid="navigate-arrow"
                onclick={() => onNavigate(neuron)}
                class="shrink-0 flex items-center justify-center gap-0.5 min-w-[44px] min-h-[44px] text-text-muted opacity-40 hover:opacity-90 transition-opacity cursor-pointer"
                aria-label="Navigate into {neuron.title}"
              >
                {#if neuron.children_count > 0}
                  <span class="text-xs tabular-nums">{neuron.children_count}</span>
                {/if}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M7 4l6 6-6 6"/>
                </svg>
              </button>
            {/if}
          </li>
        {/each}
      </ul>
    {:else if kanbanNeurons.length === 0}
      <div data-testid="empty-neuron-list"></div>
    {/if}
  {/if}
</div>

<style>
  :global(li.discarded) {
    opacity: 0.5;
  }
  :global(li.discarded [data-testid="neuron-title"]) {
    text-decoration: line-through;
  }
  :global(li.flagged) {
    opacity: 0.5;
  }
  :global(li.flagged [data-testid="neuron-title"]) {
    font-style: italic;
  }
  :global([data-testid="neuron-list"] li:has([data-testid="slash-autocomplete"])) {
    clip-path: none !important;
    z-index: 10;
  }
  :global([data-testid="neuron-list"] li) {
    clip-path: inset(0 0 0 -3rem);
    transition: opacity 200ms cubic-bezier(0.32, 0.72, 0, 1),
                filter 200ms cubic-bezier(0.32, 0.72, 0, 1),
                clip-path 200ms cubic-bezier(0.32, 0.72, 0, 1) 150ms;
  }
  :global(.empty-drop-zone) {
    min-height: 3rem;
    border-radius: 0.5rem;
    border: 1px dashed color-mix(in srgb, var(--color-border) 50%, transparent);
  }
  :global(li.fading-out) {
    opacity: 0 !important;
    filter: blur(4px);
    clip-path: inset(0 0 100% -3rem) !important;
  }
</style>
